// ============================================================================
// Resource API - Supabase data layer for shared resources & claims
// ============================================================================

import { supabase, auth } from 'app/auth/auth.js';

// ============================================================================
// Read Operations
// ============================================================================

/**
 * Fetch all active shared resources ordered by sort_order
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getSharedResources() {
    const { data, error } = await supabase
        .from('shared_resources')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

    if (error) {
        console.error('[resourceApi] Failed to fetch shared resources:', error);
        return { success: false, error: error.message };
    }
    return { success: true, data };
}

/**
 * Fetch all shared resources (including inactive) for admin management
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getAllSharedResources() {
    const { data, error } = await supabase
        .from('shared_resources')
        .select('*')
        .order('sort_order');

    if (error) {
        console.error('[resourceApi] Failed to fetch all shared resources:', error);
        return { success: false, error: error.message };
    }
    return { success: true, data };
}

/**
 * Fetch all current resource claims with claimer profile info
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getResourceClaims() {
    const { data, error } = await supabase
        .from('resource_claims')
        .select('id, resource_id, claimed_by, claimed_at, profiles(user_id, full_name, short_name, avatar_url)');

    if (error) {
        console.error('[resourceApi] Failed to fetch resource claims:', error);
        return { success: false, error: error.message };
    }
    return { success: true, data };
}

// ============================================================================
// Claim Operations
// ============================================================================

/**
 * Claim a resource for the current user
 * @param {string} resourceId - The resource to claim
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function claimResource(resourceId) {
    const userId = auth.user?.id;
    if (!userId) return { success: false, error: 'Not authenticated' };

    const { data, error } = await supabase
        .from('resource_claims')
        .insert({ resource_id: resourceId, claimed_by: userId })
        .select('id, resource_id, claimed_by, claimed_at')
        .single();

    if (error) {
        // UNIQUE violation = someone just claimed it
        if (error.code === '23505') {
            return { success: false, error: 'Resource was just claimed by someone else' };
        }
        console.error('[resourceApi] Failed to claim resource:', error);
        return { success: false, error: error.message };
    }
    return { success: true, data };
}

/**
 * Release a resource claim (delete the claim row)
 * @param {string} resourceId - The resource to release
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function releaseResource(resourceId) {
    const { error } = await supabase
        .from('resource_claims')
        .delete()
        .eq('resource_id', resourceId);

    if (error) {
        console.error('[resourceApi] Failed to release resource:', error);
        return { success: false, error: error.message };
    }
    return { success: true };
}

// ============================================================================
// Admin CRUD Operations
// ============================================================================

/**
 * Create a new shared resource
 * @param {{ name: string, icon: string, short_label: string, sort_order: number }} resource
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function createSharedResource(resource) {
    const { data, error } = await supabase
        .from('shared_resources')
        .insert(resource)
        .select()
        .single();

    if (error) {
        console.error('[resourceApi] Failed to create shared resource:', error);
        return { success: false, error: error.message };
    }
    return { success: true, data };
}

/**
 * Update an existing shared resource
 * @param {string} id - Resource ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function updateSharedResource(id, updates) {
    const { error } = await supabase
        .from('shared_resources')
        .update(updates)
        .eq('id', id);

    if (error) {
        console.error('[resourceApi] Failed to update shared resource:', error);
        return { success: false, error: error.message };
    }
    return { success: true };
}

/**
 * Delete a shared resource (cascades to claims)
 * @param {string} id - Resource ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteSharedResource(id) {
    const { error } = await supabase
        .from('shared_resources')
        .delete()
        .eq('id', id);

    if (error) {
        console.error('[resourceApi] Failed to delete shared resource:', error);
        return { success: false, error: error.message };
    }
    return { success: true };
}
